import { Tracer, TraceMetadata } from "../tracer";

/**
 * LangSmith tracer adapter
 * 
 * This adapter requires the langsmith package to be installed by the user.
 * It's not included as a direct dependency of the library.
 */
export class LangSmithTracer implements Tracer {
  /**
   * Create a traceable function using LangSmith
   * 
   * @param fn The function to trace
   * @param metadata Metadata about the trace
   * @returns The traced function
   */
  traceable<T extends (...args: any[]) => any>(
    fn: T,
    metadata: TraceMetadata
  ): T {
    try {
      // Dynamic import to avoid requiring langsmith as a direct dependency
      // Users need to install langsmith themselves to use this adapter
      const langsmithModule = require("langsmith/traceable");
      
      // We'll use the traceable function from langsmith if available
      if (typeof langsmithModule.traceable === "function") {
        return langsmithModule.traceable(fn, metadata);
      }
      
      // Fallback to the original function if langsmith/traceable is not available
      return fn;
    } catch (error) {
      // If langsmith is not installed, return the original function
      console.warn("LangSmith not found. Install it with: npm install langsmith");
      return fn;
    }
  }
}

/**
 * Helper function to register the LangSmith tracer with the tracing registry
 * 
 * @returns true if successfully registered, false otherwise
 */
export function registerLangSmithTracer(): boolean {
  try {
    // First, ensure langsmith is installed
    require.resolve("langsmith");
    
    // Import the registry dynamically to avoid circular dependencies
    const { TracerRegistry } = require("../registry");
    const registry = TracerRegistry.getInstance();
    
    // Register the LangSmith tracer
    registry.register("langsmith", new LangSmithTracer());
    
    return true;
  } catch (error) {
    console.warn("Failed to register LangSmith tracer. Make sure langsmith is installed.");
    return false;
  }
} 